import { useEffect, useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { Botao, Falha, Girando, Selo, Vazio, cx } from "../../components/ui.js";

/**
 * O que passa nesta vivência.
 *
 * O vínculo entre divulgação e vivência existia no banco desde a Fase 7, com
 * ordenação e tudo, e os procedimentos só eram chamados por teste. Na prática,
 * uma vivência criada depois da migração não tinha como receber divulgação
 * nenhuma — foi o caso da "Zaqueu - Sicomoro", com zero peças em produção.
 *
 * A escolha vive dentro da vivência, e não da divulgação, porque a pergunta que
 * se faz é "o que passa neste evento?" — e a mesma peça costuma passar em vários.
 */
export function DivulgacoesDaVivencia({
  vivenciaId,
  aoFechar,
}: {
  /** Nulo é a lista do modo "Todas as vivências". */
  vivenciaId: string | null;
  aoFechar: () => void;
}) {
  const utils = trpc.useUtils();
  const todas = trpc.divulgacoes.list.useQuery();
  const vinculadas = trpc.divulgacoes.getVivenciaLinks.useQuery({ vivenciaId });

  // A ordem é o conteúdo: `sortOrder` é a sequência em que a galeria exibe.
  const [ordem, setOrdem] = useState<number[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (vinculadas.data) setOrdem(vinculadas.data);
  }, [vinculadas.data]);

  const salvar = trpc.divulgacoes.setVivenciaLinks.useMutation({
    onSuccess: () => {
      void utils.divulgacoes.getVivenciaLinks.invalidate({ vivenciaId });
      aoFechar();
    },
    onError: (e) => setErro(e.message),
  });

  if (todas.isLoading || vinculadas.isLoading) {
    return <Girando className="mx-auto my-8 size-5 text-texto-fraco" />;
  }

  const itens = todas.data ?? [];

  if (itens.length === 0) {
    return (
      <Vazio
        titulo="Nenhuma divulgação cadastrada"
        descricao="Envie peças em Acervo → Divulgações para poder escolher aqui."
      />
    );
  }

  const escolhidas = ordem
    .map((id) => itens.find((d) => d.id === id))
    .filter((d): d is (typeof itens)[number] => d !== undefined);
  const disponiveis = itens.filter((d) => !ordem.includes(d.id));

  const mover = (de: number, para: number) => {
    if (para < 0 || para >= ordem.length) return;
    const novo = [...ordem];
    const [x] = novo.splice(de, 1);
    novo.splice(para, 0, x!);
    setOrdem(novo);
  };

  return (
    <div className="flex flex-col gap-5">
      <section>
        <p className="mb-2 text-[0.8125rem] font-medium text-texto-suave">
          {vivenciaId === null ? "Nesta lista" : "Nesta vivência"} ({escolhidas.length})
        </p>

        {escolhidas.length === 0 ? (
          <p className="rounded-peca border border-dashed border-borda px-3 py-4 text-[0.875rem] text-texto-fraco">
            Nenhuma. Neste modo a galeria vai exibir só as fotos.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {escolhidas.map((d, i) => (
              <li
                key={d.id}
                className="flex items-center gap-3 rounded-peca border border-borda bg-superficie p-2"
              >
                <Miniatura url={d.url} video={d.mediaType === "video"} />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-[0.875rem] text-texto">{d.title ?? d.originalName}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Selo tom="quieto">{i + 1}º</Selo>
                    {d.mediaType === "video" && <Selo tom="quieto">vídeo</Selo>}
                    {d.status !== "active" && <Selo tom="descartar">inativa</Selo>}
                  </div>
                </div>

                <div className="flex shrink-0 gap-1">
                  <Seta rotulo="Subir" desabilitada={i === 0} onClick={() => mover(i, i - 1)}>
                    ↑
                  </Seta>
                  <Seta
                    rotulo="Descer"
                    desabilitada={i === escolhidas.length - 1}
                    onClick={() => mover(i, i + 1)}
                  >
                    ↓
                  </Seta>
                  <Seta rotulo="Remover" onClick={() => setOrdem(ordem.filter((x) => x !== d.id))}>
                    ×
                  </Seta>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {disponiveis.length > 0 && (
        <section>
          <p className="mb-2 text-[0.8125rem] font-medium text-texto-suave">Disponíveis</p>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {disponiveis.map((d) => (
              <li key={d.id}>
                <button
                  onClick={() => setOrdem([...ordem, d.id])}
                  className="flex w-full items-center gap-2 rounded-peca border border-borda p-2 text-left transition-colors hover:border-borda-forte"
                >
                  <Miniatura url={d.url} video={d.mediaType === "video"} />
                  <span className="min-w-0 flex-1 truncate text-[0.8125rem] text-texto-suave">
                    {d.title ?? d.originalName}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {erro && <Falha mensagem={erro} />}

      <div className="flex gap-2">
        <Botao
          tom="brasa"
          onClick={() => {
            setErro(null);
            salvar.mutate({ vivenciaId, divulgacaoIds: ordem });
          }}
          carregando={salvar.isPending}
        >
          Salvar ordem
        </Botao>
        <Botao tom="quieto" onClick={aoFechar} disabled={salvar.isPending}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}

function Miniatura({ url, video }: { url: string; video: boolean }) {
  return video ? (
    <video src={url} muted playsInline className="size-10 shrink-0 rounded object-cover" />
  ) : (
    <img src={url} alt="" loading="lazy" className="size-10 shrink-0 rounded object-cover" />
  );
}

export function Seta({
  children,
  rotulo,
  desabilitada,
  onClick,
}: {
  children: string;
  rotulo: string;
  desabilitada?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={desabilitada}
      aria-label={rotulo}
      className={cx(
        "flex size-9 items-center justify-center rounded-peca border border-borda",
        "text-texto-suave transition-colors",
        desabilitada ? "opacity-30" : "hover:border-borda-forte hover:text-texto",
      )}
    >
      {children}
    </button>
  );
}
